<script lang="ts">
	import { getTranslate } from "@utils/getTranslate.js";
	import type {
		DetailResponseModel,
		HTMLPPLAccessPointWidgetElement,
		WidgetConfig,
	} from "@core/components/PPLAccessPoint.js";
	import PPLAccessPoint from "@core/components/PPLAccessPoint.svelte";
	import type { countryToIsoCodeMap } from "@core/schema/utils/countryToIsoCodeMap.js";
	import type { EuCountry } from "@core/schema/utils/euCountries.js";
	import type { Complete } from "@core/utils/Complete.js";
	import { deeplyDeleteUndefined } from "@core/utils/deeplyDeleteUndefined.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import Loader from "./Loader.svelte";
	import translations from "./PPLDeliveryPicker.translations.json" with { type: "json" };
	import Formatted from "./common/Formatted.svelte";
	import Modal from "./common/Modal.svelte";

	const translate = getTranslate(translations);

	let {
		/* eslint-disable prefer-const */
		code,
		title,
		/* eslint-enable prefer-const */
	}: { code?: string; title: string } = $props();

	const { tenant } = getGlobalTemplateProps();
	const { PPL_WIDGET_API_KEY } = getGlobalSettings();

	let dialog = $state<HTMLDialogElement>();
	let pplWidget = $state<HTMLPPLAccessPointWidgetElement>();
	let widgetError = $state<Error>();

	let activeOptions = $state<
		Pick<WidgetConfig, "codRequired" | "disabledAccessPointTypes" | "packageWeight"> & {
			country: (typeof countryToIsoCodeMap)[EuCountry];
		}
	>();
	let show = $state(false);

	function waitOneFrame(): Promise<number> {
		return new Promise<number>((resolve, reject) => {
			if (widgetError) {
				reject(widgetError);
			} else {
				window.requestAnimationFrame(resolve);
			}
		});
	}

	export async function pick(options: NonNullable<typeof activeOptions>): Promise<DetailResponseModel | undefined> {
		const htmlDialog = dialog;
		widgetError = undefined;
		activeOptions = options;
		show = true;

		try {
			do {
				await waitOneFrame();
				// eslint-disable-next-line no-unmodified-loop-condition
			} while (!htmlDialog || !pplWidget);
		} catch {
			return;
		}

		const htmlPplWidget = pplWidget;

		return new Promise((resolve) => {
			const abortController = new AbortController();

			htmlPplWidget.addEventListener(
				"ppl-accesspointwidget-select",
				(event) => {
					show = false;
					abortController.abort();
					resolve(event.detail);
				},
				{ signal: abortController.signal },
			);

			htmlDialog.addEventListener(
				"close",
				() => {
					abortController.abort();
					resolve(undefined);
				},
				{ signal: abortController.signal },
			);
		});
	}

	const { country, ...widgetOptions } = $derived(activeOptions ?? { country: undefined });

	const options = $derived<Complete<WidgetConfig>>({
		theme: "PPL_THEME",
		viewMode: "inline",
		closeButtonVisible: false,
		defaultLang: tenant.locale,
		langMenu: false,
		defaultCountry: country,
		allowedCountries: country ? [country] : undefined,
		countriesMenuDisabled: true,
		centeredToAddress: undefined,
		centeredToLat: undefined,
		centeredToLon: undefined,
		packageWeight: undefined,
		packageWidth: undefined,
		packageHeight: undefined,
		packageLength: undefined,
		codRequired: false,
		ageCheck: false,
		accessPointCode: code,
		disabledAccessPointTypes: [],
		economyMode: false,
		...deeplyDeleteUndefined(widgetOptions),
	});
</script>

<Modal bind:dialog bind:show heading={title} class="w-full max-md:py-0 h-[calc(100dvh-2rem)]">
	<div class="flex-1 min-h-[30rem] mt-4 relative overflow-hidden">
		{#if widgetError}
			<div class="absolute inset-0 flex items-center justify-center p-4 text-center text-error-500">
				<Formatted text={translate("loadError")} />
			</div>
		{:else}
			<Loader />
		{/if}
		{#if show}
			<PPLAccessPoint
				class="absolute inset-0 size-full"
				bind:instance={pplWidget}
				bind:error={widgetError}
				apiKey={PPL_WIDGET_API_KEY}
				config={options}
			/>
		{/if}
	</div>
</Modal>
